import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  addDoc,
  collection,
  getDocs,
  query,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import instagramIcon from '../assets/images/logo_instagram.png';
import whatsappIcon from '../assets/images/logo_whatsapp.png';
import tiktokIcon from '../assets/images/tiktok_logo.png';
import linkIcon from '../assets/images/logo_personal_link.png';
import phoneIcon from '../assets/images/telephone_logo.png';
import styles from './SignupPage.module.css';
import { generateUniqueIdentificationCode } from '../utils/identificationCode';
import { createKidsServiceCodeSet, hasKidsServicesFromCodes } from '../utils/kidsServices';
import { normalizeLocationFromAutocomplete } from '../utils/locationNormalization';
import CountryCodeSelect from '../components/CountryCodeSelect';
import { DEFAULT_PROVIDER_TYPE } from '../config/providerTypes';
import { PLATFORM_COUNTRY_CODES, getCountryLabel } from '../config/countries';
import { loadGoogleMaps } from '../utils/googleMapsLoader';
import { resizeImage } from '../utils/imageResize';

const initialFormState = {
  name: '',
  // V2 Location Model
  country: null, // 'FR' | 'BE' | 'CH' | null
  city: '',
  department: '', // France only
  // V1 fields kept for backward compatibility (not used in admin form)
  mainCity: '',
  additionalCities: [],
  canTravel: false,
  instagram: '',
  // Codes pays pour WhatsApp et téléphone (par défaut +32)
  whatsappCountryCode: '+32',
  whatsapp: '',
  phoneCountryCode: '+32',
  phone: '',
  tiktok: '',
  externalLink: '',
  primaryContact: '', // Réseau social primaire
  profilePhoto: '',
  profilePhotoFile: null,
  galleryPhotos: [],
  galleryPhotoFiles: [],
  description: '',
  services: [],
  coordinates: null,
};

const contactFields = [
  {
    key: 'instagram',
    label: 'Instagram',
    placeholder: "Nom d'utilisateur",
    type: 'text',
    icon: instagramIcon,
  },
  {
    key: 'tiktok',
    label: 'TikTok',
    placeholder: "Nom d'utilisateur TikTok",
    type: 'text',
    icon: tiktokIcon,
  },
  {
    key: 'whatsapp',
    label: 'WhatsApp',
    placeholder: 'Numéro de téléphone',
    type: 'tel',
    icon: whatsappIcon,
  },
  {
    key: 'phone',
    label: 'Téléphone',
    placeholder: 'Numéro de téléphone',
    type: 'tel',
    icon: phoneIcon,
  },
  {
    key: 'externalLink',
    label: 'Lien externe',
    placeholder: 'Ex: lien Calendly',
    type: 'url',
    icon: linkIcon,
  },
];

function AdminHairstylistCreatePage() {
  const { currentUser } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [formData, setFormData] = useState(initialFormState);
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [googlePlacesLoaded, setGooglePlacesLoaded] = useState(false);
  const locationInputRef = useRef(null);
  const mainCityInputRef = useRef(null);
  const additionalCitiesAutocomplete = useRef(null);
  const mainCityAutocomplete = useRef(null);

  const isAdmin = currentUser?.displayName?.toLowerCase() === 'admin';

  useEffect(() => {
    const fetchCatalog = async () => {
      setLoadingCatalog(true);
      try {
        const [categorySnap, serviceSnap] = await Promise.all([
          getDocs(query(collection(db, 'categories'), where('type', '==', DEFAULT_PROVIDER_TYPE))),
          getDocs(query(collection(db, 'services'), where('type', '==', DEFAULT_PROVIDER_TYPE))),
        ]);

        setCategories(
          categorySnap.docs
            .map((docSnap) => docSnap.data())
            .sort((a, b) => (a.label || '').localeCompare(b.label || '')),
        );

        setServices(
          serviceSnap.docs
            .map((docSnap) => {
              const data = docSnap.data();
              return {
                ...data,
                code: data.code || data.prestationCode || '',
                label: data.label || data.prestation || '',
              };
            })
            .filter((s) => s.code && s.label)
            .sort((a, b) => (a.label || '').localeCompare(b.label || '')),
        );
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to load catalog', error);
        setFeedback({ type: 'error', message: "Impossible de charger les services." });
      } finally {
        setLoadingCatalog(false);
      }
    };

    fetchCatalog();
  }, []);

  useEffect(() => {
    if (window.google?.maps?.places) {
      setGooglePlacesLoaded(true);
      return undefined;
    }

    let script = document.querySelector('script[data-google-places]');
    const handleLoad = () => setGooglePlacesLoaded(true);
    const handleError = () => {
      // eslint-disable-next-line no-console
      console.error('Impossible de charger Google Places pour la recherche de villes.');
    };

    loadGoogleMaps({ requirePlaces: true, language: language || 'fr' })
      .then(handleLoad)
      .catch(handleError);
  }, [language]);

  const addAdditionalCity = useCallback((city) => {
    if (!city) return;
    setFormData((prev) => {
      const set = new Set(prev.additionalCities);
      set.add(city);
      return { ...prev, additionalCities: Array.from(set) };
    });
  }, []);

  const handleRemoveAdditionalCity = useCallback((city) => {
    setFormData((prev) => ({
      ...prev,
      additionalCities: prev.additionalCities.filter((c) => c !== city),
    }));
  }, []);

  useEffect(() => {
    if (!googlePlacesLoaded || !formData.canTravel || !locationInputRef.current) {
      return () => {};
    }

    additionalCitiesAutocomplete.current = new window.google.maps.places.Autocomplete(
      locationInputRef.current,
      { types: ['geocode'] },
    );

    const handlePlaceChanged = () => {
      const place = additionalCitiesAutocomplete.current?.getPlace();
      if (!place) return;
      
      // Chercher un arrondissement et une ville
      const arrondissementComponent = place.address_components?.find((component) =>
        component.types.includes('sublocality_level_1')
      );
      const cityComponent = place.address_components?.find((component) =>
        component.types.some((type) =>
          ['locality', 'postal_town', 'sublocality', 'administrative_area_level_1'].includes(type),
        ),
      );
      
      // Construire le label
      let label;
      if (arrondissementComponent && cityComponent) {
        const arrondissement = arrondissementComponent.long_name; // Ex: "20e Arrondissement"
        const city = cityComponent.long_name; // Ex: "Paris"
        
        // Extraire le format court de l'arrondissement (20, 20e, 20ème, etc.)
        const arrondissementMatch = arrondissement.match(/(\d+)(e|ème|er|ère)?/i);
        const arrondissementShort = arrondissementMatch 
          ? `${arrondissementMatch[1]}${arrondissementMatch[2] || 'e'}` // Garde "20e" ou "20ème"
          : arrondissement.replace(/arrondissement/gi, '').trim(); // Fallback: enlève "arrondissement"
        
        // Format: "Paris 20e" ou "Paris 20ème"
        label = `${city} ${arrondissementShort}`.trim();
      } else if (arrondissementComponent) {
        label = arrondissementComponent.long_name;
      } else if (cityComponent) {
        label = cityComponent.long_name;
      } else {
        // Fallback: utiliser le nom du lieu retourné par Google Places
        label = place.name || locationInputRef.current?.value || '';
      }
      addAdditionalCity(label.trim());
      if (locationInputRef.current) {
        // eslint-disable-next-line no-param-reassign
        locationInputRef.current.value = '';
      }
    };

    additionalCitiesAutocomplete.current.addListener('place_changed', handlePlaceChanged);

    return () => {
      if (additionalCitiesAutocomplete.current) {
        window.google.maps.event.clearInstanceListeners(additionalCitiesAutocomplete.current);
        additionalCitiesAutocomplete.current = null;
      }
    };
  }, [addAdditionalCity, formData.canTravel, googlePlacesLoaded]);

  useEffect(() => {
    if (!googlePlacesLoaded || !mainCityInputRef.current) {
      return () => {};
    }

    // Clean up previous autocomplete if it exists
    if (mainCityAutocomplete.current) {
      window.google.maps.event.clearInstanceListeners(mainCityAutocomplete.current);
      mainCityAutocomplete.current = null;
    }

    const countryCode = formData.country ? formData.country.toLowerCase() : null;
    const opts = { types: ['geocode'] };
    if (countryCode) opts.componentRestrictions = { country: [countryCode] };

    mainCityAutocomplete.current = new window.google.maps.places.Autocomplete(mainCityInputRef.current, opts);

    const handlePlaceChanged = () => {
      const place = mainCityAutocomplete.current?.getPlace();
      if (!place) return;

      // Normalize location data using the helper function
      const normalized = normalizeLocationFromAutocomplete(place);

      // Update form data with normalized V2 fields
      setFormData((prev) => ({
        ...prev,
        country: normalized.country || prev.country, // Keep existing if normalization fails
        city: normalized.city || '',
        department: normalized.department || '',
        // V1 fields are automatically synced in the normalization function
        // but we can also set them here for consistency
        mainCity: normalized.mainCity || '',
      }));
    };

    mainCityAutocomplete.current.addListener('place_changed', handlePlaceChanged);

    return () => {
      if (mainCityAutocomplete.current) {
        window.google.maps.event.clearInstanceListeners(mainCityAutocomplete.current);
        mainCityAutocomplete.current = null;
      }
    };
  }, [googlePlacesLoaded, formData.country]);

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };
      
      // Si un champ de contact est modifié et qu'il y a plusieurs contacts remplis,
      // sélectionner "instagram" par défaut si primaryContact est vide
      if (['instagram', 'whatsapp', 'tiktok', 'externalLink'].includes(field)) {
        const filledContacts = contactFields.filter(
          (f) => (f.key === field ? value : updated[f.key])?.trim()
        );
        
        if (filledContacts.length > 1 && !updated.primaryContact) {
          // Si instagram est rempli, le sélectionner par défaut
          if (updated.instagram?.trim()) {
            updated.primaryContact = 'instagram';
          } else {
            // Sinon, sélectionner le premier contact rempli
            updated.primaryContact = filledContacts[0].key;
          }
        }
      }
      
      return updated;
    });
  };

  const handleCountryCodeChange = (field) => (newCode) => {
    setFormData((prev) => ({
      ...prev,
      [field]: newCode,
    }));
  };

  const handleCheckboxChange = (field) => (event) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.checked }));
  };

  const handleServiceToggle = (code) => {
    setFormData((prev) => {
      const set = new Set(prev.services);
      if (set.has(code)) {
        set.delete(code);
      } else {
        set.add(code);
      }
      return { ...prev, services: Array.from(set) };
    });
  };

  const handleCategoryToggle = (categoryCode) => {
    const category = servicesByCategory.find(cat => cat.categoryCode === categoryCode);
    if (!category) return;

    const categoryServiceCodes = category.services.map(s => s.code);
    const allSelected = categoryServiceCodes.every(code => formData.services.includes(code));

    setFormData((prev) => {
      const set = new Set(prev.services);
      if (allSelected) {
        // Désélectionner tous les services de cette catégorie
        categoryServiceCodes.forEach(code => set.delete(code));
      } else {
        // Sélectionner tous les services de cette catégorie
        categoryServiceCodes.forEach(code => set.add(code));
      }
      return { ...prev, services: Array.from(set) };
    });
  };

  const handleProfilePhotoUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // N'accepter que des images
    if (!file.type || !file.type.startsWith('image/')) {
      alert('Seules les images sont autorisées pour la photo de profil.');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData((prev) => ({
        ...prev,
        profilePhoto: reader.result,
        profilePhotoFile: file,
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleGalleryUpload = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    // Filtrer pour ne garder que les images
    const imageFiles = files.filter((file) => file.type && file.type.startsWith('image/'));
    if (!imageFiles.length) {
      alert('Seules les images sont autorisées pour la galerie.');
      return;
    }

    const availableSlots = 6 - formData.galleryPhotos.length;
    if (availableSlots <= 0) {
      return;
    }

    const newFiles = imageFiles.slice(0, availableSlots);
    const newPreviews = [];
    let loadedCount = 0;

    newFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        newPreviews.push(reader.result);
        loadedCount += 1;
        if (loadedCount === newFiles.length) {
          setFormData((prev) => ({
            ...prev,
            galleryPhotos: [...prev.galleryPhotos, ...newPreviews].slice(0, 6),
            galleryPhotoFiles: [...prev.galleryPhotoFiles, ...newFiles].slice(0, 6),
          }));
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const handleGalleryRemove = (index) => {
    setFormData((prev) => ({
      ...prev,
      galleryPhotos: prev.galleryPhotos.filter((_, i) => i !== index),
      galleryPhotoFiles: prev.galleryPhotoFiles.filter((_, i) => i !== index),
    }));
  };

  const handleAdditionalCityKeyDown = (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    const { value } = event.target;
    if (!value.trim()) return;
    addAdditionalCity(value.trim());
    // eslint-disable-next-line no-param-reassign
    event.target.value = '';
  };

  const toggleCategory = (code) => {
    setExpanded((prev) => ({
      ...prev,
      [code]: !prev[code],
    }));
  };

  const servicesByCategory = useMemo(() => {
    return categories.map((category) => ({
      ...category,
      services: services.filter((service) => service.categoryCode === category.categoryCode),
    }));
  }, [categories, services]);

  const kidsServiceCodeSet = useMemo(
    () => createKidsServiceCodeSet(services, categories),
    [services, categories],
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFeedback(null);

    const missing = [];
    if (!formData.name.trim()) missing.push('Nom du profil');
    if (!formData.country) missing.push('Pays');
    if (!formData.city.trim()) missing.push('Ville');
    if (!formData.services.length) missing.push('Au moins une prestation');
    // L'admin n'est pas obligé d'ajouter une photo de profil (utilisera le SVG anonyme par défaut)
    // L'admin n'est pas obligé d'ajouter 3 photos de galerie
    if (!isAdmin && formData.galleryPhotos.length < 3) missing.push('Au moins 3 photos de prestations');
    
    // Vérifier qu'au moins une info de contact est remplie
    const hasContactInfo = formData.instagram.trim() || 
                          formData.whatsapp.trim() || 
                          formData.phone?.trim() || 
                          formData.tiktok.trim() || 
                          formData.externalLink.trim();
    if (!hasContactInfo) missing.push('Au moins une information de contact');
    
    // Vérifier que le réseau social primaire est sélectionné
    if (!formData.primaryContact?.trim()) {
      missing.push('Réseau social primaire');
    }

    if (missing.length) {
      setFeedback({
        type: 'error',
        message: `Merci de compléter les champs suivants : ${missing.join(', ')}.`,
      });
      return;
    }

    setSaving(true);
    try {
      // Upload photo de profil si fournie, sinon utiliser chaîne vide (le SVG anonyme sera utilisé côté client)
      let profilePhotoURL = '';
      if (formData.profilePhotoFile) {
        const timestamp = Date.now();
        const profilePhotoRef = ref(
          storage,
          `hairstylists/${formData.name.trim().replace(/\s+/g, '_')}_${timestamp}/profile.jpg`,
        );
        await uploadBytes(profilePhotoRef, formData.profilePhotoFile);
        profilePhotoURL = await getDownloadURL(profilePhotoRef);
      }

      const filesToUpload = formData.galleryPhotoFiles.slice(0, 6 - formData.galleryPhotos.length);
      const galleryUploads = await Promise.all(
        filesToUpload.map(async (file, index) => {
          try {
            // Redimensionner l'image à 400x400px max pour optimiser le chargement dans les vignettes
            const resizedBlob = await resizeImage(file, 400, 400, 0.85);
            const timestamp = Date.now();
            const galleryPhotoRef = ref(
              storage,
              `hairstylists/${formData.name.trim().replace(/\s+/g, '_')}_${timestamp}/gallery_${index}.jpg`,
            );
            await uploadBytes(galleryPhotoRef, resizedBlob);
            return getDownloadURL(galleryPhotoRef);
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error(`Erreur upload photo galerie ${index}:`, error);
            return null;
          }
        }),
      );
      // Filtrer les valeurs null en cas d'erreur
      const validGalleryUploads = galleryUploads.filter(Boolean);

      const galleryPhotoURLs = [...formData.galleryPhotos, ...validGalleryUploads].slice(0, 6);
      const identificationCode = await generateUniqueIdentificationCode();

      // Normaliser les numéros de téléphone / WhatsApp (indicatif + chiffres, sans 0 initial)
      const buildPhoneValue = (countryCode, number) => {
        const raw = (number || '').trim();
        if (!raw) return '';
        if (raw.startsWith('+')) {
          return raw.replace(/\s+/g, '');
        }
        let digits = raw.replace(/[^0-9]/g, '');
        if (digits.startsWith('0')) {
          digits = digits.slice(1);
        }
        const code = (countryCode || '+32').trim();
        return `${code}${digits}`;
      };

      const normalizedWhatsapp = buildPhoneValue(formData.whatsappCountryCode, formData.whatsapp);
      const normalizedPhone = buildPhoneValue(formData.phoneCountryCode, formData.phone);

      await addDoc(collection(db, 'hairstylists'), {
        name: formData.name.trim(),
        // V2 Location Model
        country: formData.country,
        city: formData.city.trim(),
        department: formData.country === 'FR' && formData.department.trim() ? formData.department.trim() : null,
        // V1 fields kept for backward compatibility (derived from v2)
        mainCity: formData.city.trim(), // Keep for backward compatibility
        cities: [formData.city.trim(), ...formData.additionalCities].filter(Boolean), // Keep for backward compatibility
        description: formData.description.trim(),
        canTravel: formData.canTravel,
        kidsAccepted: hasKidsServicesFromCodes(formData.services, kidsServiceCodeSet),
        services: formData.services,
        profilePhoto: profilePhotoURL,
        galleryPhotos: galleryPhotoURLs,
        contact: {
          instagram: formData.instagram.trim(),
          whatsapp: normalizedWhatsapp,
          phone: normalizedPhone,
          tiktok: formData.tiktok.trim(),
          externalLink: formData.externalLink.trim(),
          primaryContact: formData.primaryContact.trim(),
        },
        instagram: formData.instagram.trim(),
        whatsapp: normalizedWhatsapp,
        phone: normalizedPhone,
        tiktok: formData.tiktok.trim(),
        externalLink: formData.externalLink.trim(),
        primaryContact: formData.primaryContact.trim(),
        identificationCode,
        userId: null,
        userEmail: null,
        isClaimed: false,
        accountType: 'FREE', // Les profils créés par admin sont en FREE par défaut
        subscriptionActive: false,
        subscriptionStartDate: null,
        ratingSummary: {
          averageRating: 0,
          reviewCount: 0,
        },
        type: DEFAULT_PROVIDER_TYPE, // Type de prestataire (par défaut 'hair')
        createdByAdmin: currentUser?.uid || null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setFeedback({
        type: 'success',
        message: 'Profil coiffeuse ajouté avec succès 🎉',
      });
      setFormData(initialFormState);
      setExpanded({});
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to create profile', error);
      setFeedback({
        type: 'error',
        message: "Impossible d'ajouter la coiffeuse. Merci de réessayer.",
      });
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className={styles.page}>
        <div className={styles.wrapper}>
          <p style={{ textAlign: 'center' }}>
            Cette page est réservée à l’administrateur.
          </p>
          <button type="button" className={styles.backLink} onClick={() => navigate(-1)}>
            ← Retour
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.wrapper}>
        <button type="button" className={styles.backLink} onClick={() => navigate(-1)}>
          ← Retour
        </button>
        <h1>Ajouter un profil coiffeuse</h1>
        <p className={styles.subtitle}>
          Ce formulaire permet d’ajouter une coiffeuse dans l’annuaire sans créer de compte utilisateur.
        </p>

        <form className={styles.form} onSubmit={handleSubmit}>
          <section className={styles.card}>
            <div className={styles.sectionHeader}>
              <span>1.</span>
              <div>
                <h2>Informations générales</h2>
                <p>Ajoute toutes les informations nécessaires pour représenter la coiffeuse.</p>
              </div>
            </div>

            <div className={styles.fieldGrid}>
              <label>
                Nom du profil
                <input
                  type="text"
                  value={formData.name}
                  onChange={handleInputChange('name')}
                  placeholder="Ex: Aya Braid Studio"
                  required
                />
              </label>
              <label>
                Pays
                <select
                  value={formData.country || ''}
                  onChange={handleInputChange('country')}
                  required
                >
                  <option value="">Sélectionner un pays</option>
                  {PLATFORM_COUNTRY_CODES.map((code) => (
                    <option key={code} value={code}>{getCountryLabel(code, 'fr')}</option>
                  ))}
                </select>
              </label>
              <label>
                Ville
                <input
                  type="text"
                  ref={mainCityInputRef}
                  value={formData.city}
                  onChange={handleInputChange('city')}
                  placeholder="Ex: Paris"
                  required
                />
              </label>
              {formData.country === 'FR' && (
                <label>
                  Département
                  <input
                    type="text"
                    value={formData.department}
                    onChange={handleInputChange('department')}
                    placeholder="Ex: 75, 92, 93..."
                  />
                </label>
              )}
            </div>

            <div className={styles.locationRow}>
              <div className={styles.travelToggle}>
                <span>Je peux me déplacer au domicile de la cliente</span>
                <label className={styles.switch}>
                  <input
                    type="checkbox"
                    checked={formData.canTravel}
                    onChange={handleCheckboxChange('canTravel')}
                  />
                  <span className={styles.slider} />
                </label>
              </div>
              {formData.canTravel && (
                <div className={styles.addressSearch}>
                  <input
                    type="text"
                    ref={locationInputRef}
                    placeholder="Ajoute une ville desservie"
                    onKeyDown={handleAdditionalCityKeyDown}
                  />
                </div>
              )}
            </div>
            {formData.canTravel && formData.additionalCities.length > 0 && (
              <div className={styles.cityChips}>
                {formData.additionalCities.map((city) => (
                  <span key={city} className={styles.cityChip}>
                    {city}
                    <button type="button" onClick={() => handleRemoveAdditionalCity(city)}>
                      ✕
                    </button>
                  </span>
                ))}
              </div>
            )}


            <label>
              Présentation
              <textarea
                rows={4}
                value={formData.description}
                onChange={handleInputChange('description')}
                placeholder="Décris comment elle fonctionne, ce qu'elle propose, ses prestations…"
              />
            </label>
          </section>

          <section className={styles.card}>
            <div className={styles.sectionHeader}>
              <span>2.</span>
              <div>
                <h2>Coordonnées</h2>
                <p>Comment les clientes peuvent la contacter ?</p>
              </div>
            </div>

            <div className={styles.contactsBox}>
              {contactFields.map((field) => (
                <label key={field.key} className={styles.contactField}>
                  <span className={styles.contactLabel}>
                    <img src={field.icon} alt={field.label} />
                    {field.label}
                  </span>
                  {(field.key === 'whatsapp' || field.key === 'phone') ? (
                    <div className={styles.phoneInputRow}>
                      <CountryCodeSelect
                        value={field.key === 'whatsapp' ? formData.whatsappCountryCode : formData.phoneCountryCode}
                        onChange={handleCountryCodeChange(
                          field.key === 'whatsapp' ? 'whatsappCountryCode' : 'phoneCountryCode',
                        )}
                      />
                      <input
                        type={field.type}
                        value={formData[field.key]}
                        onChange={handleInputChange(field.key)}
                        placeholder={field.placeholder}
                      />
                    </div>
                  ) : (
                    <input
                      type={field.type}
                      value={formData[field.key]}
                      onChange={handleInputChange(field.key)}
                      placeholder={field.placeholder}
                    />
                  )}
                </label>
              ))}
            </div>

            <div className={styles.primaryContactSection}>
              <div className={styles.primaryContactRow}>
                <label className={styles.primaryContactLabel}>
                  Réseau social primaire
                </label>
                <select
                  value={formData.primaryContact}
                  onChange={handleInputChange('primaryContact')}
                  className={styles.primaryContactSelect}
                  required
                >
                  <option value="">Sélectionner</option>
                  {contactFields.map((field) => (
                    formData[field.key]?.trim() && (
                      <option key={field.key} value={field.key}>
                        {field.label}
                      </option>
                    )
                  ))}
                </select>
              </div>
              <p className={styles.helpText}>
                Le réseau primaire apparaîtra en grand sur la vignette. Les autres réseaux apparaîtront en petit en dessous.
              </p>
            </div>
          </section>

          <section className={styles.card}>
            <div className={styles.sectionHeader}>
              <span>3.</span>
              <div>
                <h2>Photos</h2>
                <p>Ajoute une photo de profil et quelques réalisations (max. 6).</p>
              </div>
            </div>

            <div className={styles.photoRow}>
              <div className={styles.photoUpload}>
                {formData.profilePhoto ? (
                  <>
                    <img src={formData.profilePhoto} alt="Profil" />
                    <button
                      type="button"
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          profilePhoto: '',
                          profilePhotoFile: null,
                        }))
                      }
                    >
                      Retirer
                    </button>
                  </>
                ) : (
                  <>
                    <span>Photo de profil</span>
                    <label className={styles.uploadBtn}>
                      Ajouter photo
                      <input type="file" accept="image/*" onChange={handleProfilePhotoUpload} />
                    </label>
                  </>
                )}
              </div>

              <div className={styles.galleryUpload}>
                <div className={styles.galleryList}>
                  {formData.galleryPhotos.map((photo, index) => (
                    <div key={`${photo}-${index}`} className={styles.galleryItem}>
                      <img src={photo} alt={`Galerie ${index + 1}`} />
                      <button type="button" onClick={() => handleGalleryRemove(index)}>
                        Retirer
                      </button>
                    </div>
                  ))}
                  {formData.galleryPhotos.length < 6 && (
                    <label className={styles.galleryAdd}>
                      +
                      <input type="file" accept="image/*" multiple onChange={handleGalleryUpload} />
                    </label>
                  )}
                </div>
              </div>
            </div>
          </section>

          <section className={styles.card}>
            <div className={styles.sectionHeader}>
              <span>4.</span>
              <div>
                <h2>Prestations proposées</h2>
                <p>Sélectionne toutes les prestations correspondantes.</p>
              </div>
            </div>

            {loadingCatalog && <p>Chargement des catégories…</p>}
            {!loadingCatalog &&
              servicesByCategory.map((category) => {
                const categoryServiceCodes = category.services.map(s => s.code);
                const allSelected = categoryServiceCodes.length > 0 && 
                  categoryServiceCodes.every(code => formData.services.includes(code));
                const someSelected = categoryServiceCodes.some(code => formData.services.includes(code));

                return (
                  <div key={category.categoryCode} className={styles.accordion}>
                    <button
                      type="button"
                      className={styles.accordionHeader}
                      onClick={() => toggleCategory(category.categoryCode)}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <input
                          type="checkbox"
                          checked={allSelected}
                          ref={(el) => {
                            if (el) {
                              el.indeterminate = someSelected && !allSelected;
                            }
                          }}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleCategoryToggle(category.categoryCode);
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <span>{category.label}</span>
                      </div>
                      <span>{expanded[category.categoryCode] ? '▴' : '▾'}</span>
                    </button>
                    {expanded[category.categoryCode] && (
                      <div className={styles.serviceGrid}>
                        {category.services.map((service) => (
                          <label key={service.code}>
                            <input
                              type="checkbox"
                              checked={formData.services.includes(service.code)}
                              onChange={() => handleServiceToggle(service.code)}
                            />
                            <span>{service.label}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
          </section>

          {feedback && (
            <div className={`${styles.feedback} ${styles[feedback.type]}`}>
              {feedback.message}
            </div>
          )}

          <button type="submit" className={styles.submit} disabled={saving}>
            {saving ? 'Enregistrement...' : 'Ajouter la coiffeuse'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default AdminHairstylistCreatePage;

